import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { OfferedCreditPackId as CreditPackId } from 'fym-shared/src/music24/creditPacks';
import { useTRPCAPI, useTRPCMutation } from '../../../../trpc/trpc';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { getUpgradeSessionErrorMessage } from '../../utils/getUpgradeSessionErrorMessage';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { hasTrackingConsent } from '../../../analytics/consent/trackingConsent';
import { saveCreditPackReturn } from './creditPackReturn';
import { useDisplayCurrency } from '../../config/useDisplayCurrency';

export interface BuyCreditPack {
  /** False for a member (only the owner buys, D11) and inside a native app's web view (App Store 3.1.1). */
  canBuy: boolean;
  /** The pack whose checkout is opening, else null. */
  loadingPackId: CreditPackId | null;
  buy: (packId: CreditPackId) => Promise<void>;
}

/**
 * Buys a video-credit pack: a one-time Stripe Checkout (`billing.createMusic24CreditPackSession`).
 * Stripe returns to `/app/paymentComplete?…&creditPack=1`; a cancel returns to this page. Before
 * the redirect, the page address and the draft on screen go to the tab storage
 * (`saveCreditPackReturn`), so the payment page sends the user back to the same step with the
 * same draft. The Meta Purchase event goes only with the visitor's Accept (`trackingConsent`).
 */
export function useBuyCreditPack(): BuyCreditPack {
  const intl = useIntl();
  const trpcAPI = useTRPCAPI();
  const { showError } = useShowToast();
  const [loadingPackId, setLoadingPackId] = useState<CreditPackId | null>(null);
  const isMember = useMyTeamRole() === 'MEMBER';
  const isNativeEmbed = useIsNativeEmbed();
  const canBuy = !isMember && !isNativeEmbed;
  // Checkout charges the currency of the pack prices on screen (QA finding D12, decision D7).
  const currency = useDisplayCurrency();

  const { mutateAsync: createCreditPackSession } = useTRPCMutation(
    trpcAPI.billing.createMusic24CreditPackSession.mutationOptions()
  );

  const buy = useCallback(
    async (packId: CreditPackId): Promise<void> => {
      if (!canBuy) {
        return;
      }
      setLoadingPackId(packId);
      try {
        const result = await createCreditPackSession({
          packId,
          returnUrl: window.location.href,
          trackingConsent: hasTrackingConsent(),
          currency,
        });
        if (result.url) {
          saveCreditPackReturn(window.location.href);
          window.location.href = result.url;
        }
      } catch (error) {
        // The raw error can carry Stripe text: log it, show only the translated message.
        console.error('Failed to create the credit pack session', error);
        showError(getUpgradeSessionErrorMessage(error, intl));
      } finally {
        setLoadingPackId(null);
      }
    },
    [canBuy, createCreditPackSession, currency, showError, intl]
  );

  return { canBuy, loadingPackId, buy };
}
